/*
 * Right panel — Inspector (UI spec §4.7). Selected clip name, source block
 * (Source, Author, Licence chip, Location), Gain + Pan with mono readouts and a
 * centre detent, then EQ (5-band curve on an inset well) and Reverb (preset +
 * Size/Decay/Wet). EQ and Reverb appear only in Advanced mode.
 */

import { store, type AppState } from '../state/store';
import { isInteracting, onGestureEnd } from './interaction';
import { clear, h, fmtDb } from './dom';
import { tt } from './tooltip';
import { licenceName, licenceShort, licenceTone, ALL_LICENCE_IDS, makeLicence } from '../licence/model';
import {
  setClipFade,
  setClipLoop,
  setClipGain,
  setClipPan,
  setTrackPan,
  updateAssetAuthor,
  updateAssetLicence,
} from '../state/edits';
import {
  setAutomationView,
  toggleEq,
  toggleReverb,
  setEqBand,
  setReverbParam,
  setReverbPreset,
} from '../state/effectEdits';
import { REVERB_PRESETS } from '../audio/effects/reverb';
import type { AutomationParam, Clip, EqSettings, Track } from '../state/project';
import { AUTOMATION_PARAMS } from './automationParams';

const fmtPan = AUTOMATION_PARAMS.pan.format;
/** Within this of centre, a pan slider settles on dead centre. */
const PAN_DETENT = 0.04;
const detent = (v: number) => (Math.abs(v) < PAN_DETENT ? 0 : v);

export class Inspector {
  readonly el: HTMLElement;

  constructor() {
    // The panel chrome (width, border, tabs) belongs to RightPanel; this is
    // just the scrolling pane inside it.
    this.el = h('div', { class: 'pane-body scroll' });
    this.render(store.get());
    store.subscribe((s, changed) => {
      if (!changed.has('ui') && !changed.has('project')) return;
      // A slider's oninput mutates the project; re-rendering here would
      // replace the slider under the pointer. Controls update their own
      // readouts, so just defer the rebuild until the gesture ends.
      if (isInteracting()) {
        onGestureEnd('inspector', () => this.render(store.get()));
        return;
      }
      this.render(s);
    });
  }

  private selected(s: AppState): { track: Track; clip: Clip } | null {
    const { trackId, clipId } = s.ui.selection;
    if (!trackId) return null;
    const track = s.project.tracks.find((t) => t.id === trackId);
    if (!track) return null;
    const clip = track.clips.find((c) => c.id === clipId);
    return clip ? { track, clip } : null;
  }

  private render(s: AppState): void {
    clear(this.el);
    const sel = this.selected(s);
    if (!sel) {
      this.el.append(
        h('div', { class: 'empty' },
          s.ui.selection.trackId
            ? 'Select a clip to edit its source, gain, fades and effects.'
            : 'Select a track or clip. Its properties appear here.'),
      );
      // still show the track's own controls if a track is selected
      if (s.ui.selection.trackId) {
        const t = s.project.tracks.find((x) => x.id === s.ui.selection.trackId);
        if (t) this.el.append(this.trackSection(s, t));
        if (t && s.ui.mode === 'advanced') {
          this.el.append(this.eqSection(t), this.reverbSection(t));
        }
      }
      return;
    }

    const { track, clip } = sel;
    const ref = s.project.assets[clip.assetId];

    // --- source block ---
    const tone = ref ? licenceTone(ref.licence) : 'unknown';
    const licenceChip = h(
      'span',
      { class: `lic ${tone}`, ...tt(ref ? licenceName(ref.licence.id) : 'Unknown', 'Change the licence below if you know it.') },
      ref ? licenceShort(ref.licence) : '?',
    );

    this.el.append(
      h(
        'div',
        { class: 'insp-section' },
        h('div', { class: 'section-label' }, 'SELECTED CLIP'),
        h('div', { class: 'insp-title', style: 'margin-top:7px' }, ref?.title ?? 'Clip'),
        h('div', { style: 'margin-top:11px' },
          kv('Source', ref?.source ?? '—'),
          this.editableAuthor(ref?.id, ref?.author ?? '—'),
          h('div', { class: 'kv' }, h('span', { class: 'k' }, 'Licence'), licenceChip),
          this.licencePicker(ref?.id, ref?.licence.id),
          ref?.location
            ? kv('Location', `${ref.location.lat.toFixed(4)} ${ref.location.lat >= 0 ? 'N' : 'S'} ${Math.abs(ref.location.lon).toFixed(4)} ${ref.location.lon >= 0 ? 'E' : 'W'}`, true)
            : document.createComment('no-loc'),
        ),
      ),
    );

    // --- gain / pan (clip gain) ---
    this.el.append(
      h(
        'div',
        { class: 'insp-section' },
        sliderRow(
          'Clip gain',
          `${fmtDb(clip.gain)} dB`,
          clip.gain,
          -40,
          12,
          0.5,
          (v) => setClipGain(track.id, clip.id, v),
          (v) => `${fmtDb(v)} dB`,
          0,
        ),
        sliderRow(
          'Clip pan',
          fmtPan(clip.pan ?? 0),
          clip.pan ?? 0,
          -1,
          1,
          0.01,
          (v) => setClipPan(track.id, clip.id, detent(v)),
          (v) => fmtPan(detent(v)),
          0,
        ),
        sliderRow(
          'Fade in',
          `${clip.fadeIn.duration.toFixed(2)} s`,
          clip.fadeIn.duration,
          0,
          6,
          0.05,
          (v) => setClipFade(track.id, clip.id, 'fadeIn', v),
          (v) => `${v.toFixed(2)} s`,
        ),
        this.fadeCurvePick(track.id, clip.id, 'fadeIn', clip.fadeIn.curve),
        sliderRow(
          'Fade out',
          `${clip.fadeOut.duration.toFixed(2)} s`,
          clip.fadeOut.duration,
          0,
          6,
          0.05,
          (v) => setClipFade(track.id, clip.id, 'fadeOut', v),
          (v) => `${v.toFixed(2)} s`,
        ),
        this.fadeCurvePick(track.id, clip.id, 'fadeOut', clip.fadeOut.curve),
        sliderRow(
          'Loop ×',
          String(clip.loop?.count ?? 1),
          clip.loop?.count ?? 1,
          1,
          16,
          1,
          (v) => setClipLoop(track.id, clip.id, v, clip.loop?.crossfade ?? 0.05),
          (v) => String(v),
        ),
      ),
    );

    this.el.append(this.trackSection(s, track));
    if (s.ui.mode === 'advanced') {
      this.el.append(this.eqSection(track), this.reverbSection(track));
    }
  }

  /**
   * The track the selection sits on. Track pan was in the data model, the
   * audio graph and the automation list, but had no control anywhere.
   */
  private trackSection(s: AppState, track: Track): HTMLElement {
    const automated = (param: AutomationParam) =>
      track.automation.some((l) => l.param === param && l.enabled && l.points.length > 0);
    const panAutomated = automated('pan');
    return h(
      'div',
      { class: 'insp-section' },
      h(
        'div',
        { class: 'insp-h' },
        h('span', { class: 'insp-title' }, track.name),
        h('span', { class: 'mono-cap' }, 'TRACK'),
      ),
      sliderRow(
        panAutomated ? 'Track pan (automated)' : 'Track pan',
        fmtPan(track.pan),
        track.pan,
        -1,
        1,
        0.01,
        (v) => setTrackPan(track.id, detent(v)),
        (v) => fmtPan(detent(v)),
        0,
      ),
      s.ui.mode === 'advanced'
        ? h(
            'div',
            { class: 'automate-links' },
            h('span', { class: 'k' }, 'Automate'),
            ...(['gain', 'pan'] as const).map((param) =>
              h(
                'button',
                {
                  class: `linkish${automated(param) ? ' on' : ''}`,
                  ...tt(
                    `Draw a ${AUTOMATION_PARAMS[param].label.toLowerCase()} curve`,
                    'Opens the automation lane on this track. Double-click the lane to add points.',
                  ),
                  onclick: () => setAutomationView(track.id, param),
                },
                AUTOMATION_PARAMS[param].label,
              ),
            ),
          )
        : null,
    );
  }

  private editableAuthor(assetId: string | undefined, value: string): HTMLElement {
    if (!assetId) return kv('Author', value);
    return h(
      'div',
      { class: 'kv' },
      h('span', { class: 'k' }, 'Author'),
      h('input', {
        class: 'v',
        style: 'text-align:right;background:none;border:none;max-width:150px',
        value,
        onchange: (e) => updateAssetAuthor(assetId, (e.target as HTMLInputElement).value),
      }),
    );
  }

  private licencePicker(assetId: string | undefined, current: string | undefined): HTMLElement {
    if (!assetId) return document.createComment('no-asset') as unknown as HTMLElement;
    return h(
      'div',
      { class: 'kv' },
      h('span', { class: 'k' }, 'Set licence'),
      h(
        'select',
        {
          style: 'font-size:11px',
          onchange: (e) => updateAssetLicence(assetId, makeLicence((e.target as HTMLSelectElement).value as never)),
        },
        ...ALL_LICENCE_IDS.map((id) =>
          h('option', { value: id, selected: id === current }, licenceName(id)),
        ),
      ),
    );
  }

  private fadeCurvePick(
    trackId: string,
    clipId: string,
    which: 'fadeIn' | 'fadeOut',
    curve: 'linear' | 'equalPower',
  ): HTMLElement {
    return h(
      'div',
      { class: 'kv', ...tt('Fade curve', 'Equal-power keeps perceived loudness steady through the blend; linear is a straight level ramp.') },
      h('span', { class: 'k' }, `${which === 'fadeIn' ? 'In' : 'Out'} curve`),
      h(
        'select',
        {
          style: 'font-size:11px',
          onchange: (e) =>
            setClipFade(trackId, clipId, which, -1, (e.target as HTMLSelectElement).value as 'linear' | 'equalPower'),
        },
        h('option', { value: 'equalPower', selected: curve === 'equalPower' }, 'Equal-power'),
        h('option', { value: 'linear', selected: curve === 'linear' }, 'Linear'),
      ),
    );
  }

  private eqSection(track: Track): HTMLElement {
    const eq = track.eq;
    const enabled = !!eq?.enabled;
    const well = h('div', { class: 'eq-well' });
    if (eq) well.append(this.eqCurve(eq));

    return h(
      'div',
      { class: 'insp-section' },
      h(
        'div',
        { class: 'insp-h' },
        h('span', { class: 'insp-title' }, 'Equaliser'),
        h('button', { class: `toggle${enabled ? ' on' : ''}`, onclick: () => toggleEq(track.id) }, h('span', { class: 'knob' })),
      ),
      well,
      h('div', { class: 'eq-scale' }, ...['60', '250', '1k', '4k', '12k'].map((f) => h('span', {}, f))),
      ...(eq
        ? eq.bands.map((b, i) =>
            sliderRow(
              ['Low', 'Low mid', 'Mid', 'High mid', 'High'][i],
              `${b.gain > 0 ? '+' : ''}${b.gain.toFixed(1)} dB`,
              b.gain,
              -18,
              18,
              0.5,
              (v) => setEqBand(track.id, i, { gain: v }),
              (v) => `${v > 0 ? '+' : ''}${v.toFixed(1)} dB`,
            ),
          )
        : []),
    );
  }

  private eqCurve(eq: EqSettings): SVGSVGElement {
    const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
    svg.setAttribute('viewBox', '0 0 260 76');
    svg.setAttribute('preserveAspectRatio', 'none');
    svg.setAttribute('style', 'position:absolute;inset:0;width:100%;height:100%');
    const mid = document.createElementNS('http://www.w3.org/2000/svg', 'line');
    mid.setAttribute('x1', '0');
    mid.setAttribute('y1', '38');
    mid.setAttribute('x2', '260');
    mid.setAttribute('y2', '38');
    mid.setAttribute('stroke', 'var(--border)');
    svg.append(mid);
    const path = document.createElementNS('http://www.w3.org/2000/svg', 'path');
    const pts = eq.bands.map((b, i) => {
      const x = (i / (eq.bands.length - 1)) * 260;
      const y = 38 - (b.gain / 18) * 30;
      return `${x.toFixed(1)},${y.toFixed(1)}`;
    });
    path.setAttribute('d', `M ${pts.join(' L ')}`);
    path.setAttribute('fill', 'none');
    path.setAttribute('stroke', 'var(--accent)');
    path.setAttribute('stroke-width', '1.6');
    svg.append(path);
    return svg;
  }

  private reverbSection(track: Track): HTMLElement {
    const rv = track.reverb;
    const enabled = !!rv?.enabled;
    const presetKey = (rv?.preset ?? 'street') as keyof typeof REVERB_PRESETS | 'custom';
    const def = presetKey !== 'custom' ? REVERB_PRESETS[presetKey] : null;

    return h(
      'div',
      { class: 'insp-section' },
      h(
        'div',
        { class: 'insp-h' },
        h('span', { class: 'insp-title' }, 'Reverb'),
        h('button', { class: `toggle${enabled ? ' on' : ''}`, onclick: () => toggleReverb(track.id) }, h('span', { class: 'knob' })),
      ),
      h(
        'div',
        {
          class: 'select',
          style: 'margin-bottom:13px',
          ...tt(
            `Spatial preset: ${def?.label ?? 'Custom'}`,
            def ? `Sets size ${Math.round(def.size * 100)}% and decay ${def.decay} s — ${def.blurb}.` : 'Your own settings.',
          ),
        },
        h(
          'select',
          {
            onchange: (e) => setReverbPreset(track.id, (e.target as HTMLSelectElement).value as never),
          },
          ...Object.entries(REVERB_PRESETS).map(([k, v]) =>
            h('option', { value: k, selected: k === presetKey }, v.label),
          ),
          h('option', { value: 'custom', selected: presetKey === 'custom', disabled: true }, 'Custom'),
        ),
      ),
      sliderRow(
        'Size',
        `${Math.round((rv?.size ?? 0.34) * 100)}%`,
        rv?.size ?? 0.34, 0, 1, 0.01,
        (v) => setReverbParam(track.id, { size: v }),
        (v) => `${Math.round(v * 100)}%`,
      ),
      sliderRow(
        'Decay',
        `${(rv?.decay ?? 0.9).toFixed(1)} s`,
        rv?.decay ?? 0.9, 0.1, 6, 0.1,
        (v) => setReverbParam(track.id, { decay: v }),
        (v) => `${v.toFixed(1)} s`,
      ),
      sliderRow(
        'Wet',
        `${Math.round((rv?.wet ?? 0.18) * 100)}%`,
        rv?.wet ?? 0.18, 0, 1, 0.01,
        (v) => setReverbParam(track.id, { wet: v }),
        (v) => `${Math.round(v * 100)}%`,
      ),
    );
  }
}

function kv(k: string, v: string, mono = false): HTMLElement {
  return h('div', { class: 'kv' }, h('span', { class: 'k' }, k), h('span', { class: `v${mono ? ' mono' : ''}` }, v));
}

/**
 * `format` lets the row update its OWN readout on input. Without it the panel
 * had to re-render to show the new number, which replaced the slider being
 * dragged and killed the gesture after one step.
 */
function sliderRow(
  label: string,
  valueText: string,
  value: number,
  min: number,
  max: number,
  step: number,
  onInput: (v: number) => void,
  format?: (v: number) => string,
  /** double-clicking the slider puts it back here (0 dB, centre) */
  reset?: number,
): HTMLElement {
  const valEl = h('span', { class: 'sr-val' }, valueText);
  const set = (v: number) => {
    if (format) valEl.textContent = format(v);
    onInput(v);
  };
  return h(
    'div',
    { class: 'slider-row', ...(reset !== undefined ? tt(label, 'Double-click to reset') : {}) },
    h('div', { class: 'sr-h' }, h('span', {}, label), valEl),
    h('input', {
      type: 'range',
      min: String(min),
      max: String(max),
      step: String(step),
      value: String(value),
      'aria-label': label,
      oninput: (e) => set(Number((e.target as HTMLInputElement).value)),
      ondblclick:
        reset !== undefined
          ? (e) => {
              (e.target as HTMLInputElement).value = String(reset);
              set(reset);
            }
          : undefined,
    }),
  );
}
